import { loadAlbumWindow } from './albumWindow';
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemberEntry } from '../members/memberMemory';
import { restoreSearchScroll } from '../search/useSearchScrollMemory';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { browseList } from './api';
import { loadReviewWindow } from './listModel';

export function useBrowseSession(kind) {
  const { locale } = useWebLocale();
  return useMemberEntry(`browse60:${kind}:${locale}`);
}
export function useBrowseContext(entry) {
  const ref = useRef(
    entry.browse || { id: `${Date.now()}-${Math.random()}`, tabs: {}, sessions: {} },
  );
  if (entry.browse) ref.current = entry.browse;
  else entry.browse = ref.current;
  return ref.current;
}
export function useBrowseQuery(kind, args, identity, { enabled = true } = {}) {
  const client = useQueryClient();
  const { page, ...inputs } = args;
  const family = kind === 'albums' ? 'reviewsAlbums' : 'browse-reviews';
  const base = [family, 'list60', identity, inputs];
  const query = useQuery({
    queryKey: [...base, page],
    enabled,
    queryFn: ({ signal }) => {
      if (kind === 'albums' && !args.windowSize) return browseList(kind, args, signal);
      const seed = client
        .getQueryCache()
        .findAll({ queryKey: base })
        .filter((q) => !q.state.isInvalidated)
        .map((q) => q.state.data?.source)
        .filter(Boolean)
        .sort((a, b) => b.pages.length - a.pages.length)[0];
      return kind === 'albums'
        ? loadAlbumWindow(args, signal, browseList, seed)
        : loadReviewWindow(args, signal, browseList, seed);
    },
    retry: false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  const key = JSON.stringify(base);
  const previous = useRef(null);
  if (query.data) previous.current = { key, page };
  const data =
    query.data ||
    (previous.current?.key === key
      ? client.getQueryData([...base, previous.current.page])
      : undefined);
  const refresh = () => client.invalidateQueries({ queryKey: [family, 'list60', identity] });
  return { ...query, data, initialLoading: query.isPending && !data, refresh };
}
export function useBrowsePosition({ entry, returning, ready }) {
  const root = useRef(null);
  const restoring = useRef(false);
  const previous = useRef(null);
  if (previous.current !== entry) {
    previous.current = entry;
    restoring.current = returning && entry.y !== undefined;
  }
  useEffect(() => {
    if (!ready || !restoring.current) return;
    let alive = true,
      stop,
      stopFocus;
    const timer = setTimeout(() => {
      if (!entry.current()) return;
      stop = restoreSearchScroll(entry.y || 0, () => {
        if (!alive) return;
        restoring.current = false;
        if (entry.current() && entry.focus) {
          const focusSavedLink = () => {
            const links = Array.from(root.current?.querySelectorAll('a') || []).filter(
              (link) => link.getAttribute('href') === entry.focus,
            );
            const target = entry.focusKey
              ? links.find((link) => link.getAttribute('data-browse-focus-key') === entry.focusKey)
              : links.find(
                  (link) =>
                    (link.getAttribute('aria-label') || link.textContent) === entry.focusLabel,
                ) ||
                links.find((link) => link.getClientRects().length) ||
                links[0];
            target?.focus({ preventScroll: true });
            return Boolean(target);
          };
          // A delayed rail may mount after the page is already tall enough to restore.
          // Wait for that link, but never move focus after fresh user interaction.
          if (!focusSavedLink() && root.current && typeof MutationObserver !== 'undefined') {
            const observer = new MutationObserver(() => {
              if (!entry.current() || focusSavedLink()) stopFocus?.();
            });
            const timer = setTimeout(() => stopFocus?.(), 5000);
            const cancel = () => stopFocus?.();
            stopFocus = () => {
              observer.disconnect();
              clearTimeout(timer);
              for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel'])
                window.removeEventListener(type, cancel, true);
            };
            observer.observe(root.current, { childList: true, subtree: true });
            for (const type of ['pointerdown', 'keydown', 'touchstart', 'wheel'])
              window.addEventListener(type, cancel, { capture: true, passive: true });
          }
        }
      });
    }, 0);
    return () => {
      alive = false;
      clearTimeout(timer);
      stop?.();
      stopFocus?.();
    };
  }, [entry, ready]);
  useEffect(() => {
    const save = () => {
      if (entry.current() && !restoring.current) entry.y = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [entry]);
  const remember = (event) => {
    const link = event.target.closest?.('a');
    if (!link || !entry.current()) return;
    entry.y = window.scrollY;
    entry.focus = link.getAttribute('href');
    entry.focusKey = link.getAttribute('data-browse-focus-key');
    entry.focusLabel = link.getAttribute('aria-label') || link.textContent;
  };
  return { ref: root, onClickCapture: remember, onFocusCapture: remember };
}
